import { useCallback } from "react";
import { Country } from "@lookiero/sty-psp-locale";
import { AnswersValidation } from "@lookiero/sty-psp-quiz-style-profile-common-ui";
import { Segment } from "@lookiero/sty-psp-segment";
import { TrackingEventCategory, useEmitUserEvent } from "@lookiero/sty-psp-tracking";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { PROJECT, QuestionErrorTrackingEvent, TrackingEventName } from "./tracking";

interface TrackQuestionErrorFunctionArgs {
  readonly validation: AnswersValidation[] | undefined;
}

interface TrackQuestionErrorFunction {
  (args: TrackQuestionErrorFunctionArgs): void;
}

interface UseTrackQuestionErrorFunctionArgs {
  readonly slug: string | undefined;
  readonly country: Country;
  readonly segment: Segment;
  readonly customerId: string;
  readonly group: string;
  readonly tradename: Tradename;
}

interface UseTrackQuestionErrorFunction {
  (agrs: UseTrackQuestionErrorFunctionArgs): TrackQuestionErrorFunction;
}

const useTrackQuestionError: UseTrackQuestionErrorFunction = ({
  slug,
  country,
  segment,
  customerId,
  tradename,
  group,
}) => {
  const emitUserEvent = useEmitUserEvent<QuestionErrorTrackingEvent>();

  const trackQuestionError: TrackQuestionErrorFunction = useCallback(
    ({ validation }) => {
      if (!validation) {
        return;
      }

      validation.forEach((v) => {
        const errorType = v.value ? "format" : v.mandatory && v.ctaTriggered ? "mandatory" : undefined;

        if (!errorType) {
          return;
        }

        emitUserEvent({
          event: TrackingEventName.QUESTION_ERROR,
          eventCategory: TrackingEventCategory.NAVIGATION,
          section: `${PROJECT}_${slug}`,
          store: country,
          segment,
          userId: customerId,
          tradename,
          group,
          errorType,
        });
      });
    },
    [country, customerId, tradename, emitUserEvent, group, segment, slug],
  );

  return trackQuestionError;
};

export { useTrackQuestionError };
